import { normalizeMarketingConfig } from "./normalizer";

const TEST_MARKETING_TYPES = ["banner", "popup", "video", "ruleta"];
const TEST_MARKETING_TYPE_ALIASES = {
  roulette: "ruleta",
};
const TEST_MARKETING_TARGET = {
  href: "https://www.elcorral.com/",
  openIn: "new_tab",
};
const DEFAULT_TEST_HEADER_BOTTOM_OFFSET_PX = 24;

const TEST_ROULETTE_PRIZES = [
  { angle: 0, label: "5 créditos" },
  { angle: 45, label: "VIP por 3 días" },
  { angle: 90, label: "2 créditos" },
  { angle: 135, label: "2X puntos por 1 hora" },
  { angle: 180, label: "100 puntos" },
  { angle: 225, label: "50 puntos" },
  { angle: 270, label: "25 puntos" },
  { angle: 315, label: "10 créditos" },
];

const normalizeRequestedTypes = (search) => {
  if (typeof search !== "string" || search.trim() === "") return [];

  const params = new URLSearchParams(search);
  const rawValues = params.getAll("testmkt");
  if (rawValues.length === 0) return [];

  const tokens = rawValues
    .join(",")
    .replaceAll("[", "")
    .replaceAll("]", "")
    .split(",")
    .map((value) => {
      const token = value.trim().toLowerCase();
      return TEST_MARKETING_TYPE_ALIASES[token] ?? token;
    })
    .filter(Boolean);

  if (tokens.includes("all")) return TEST_MARKETING_TYPES;

  return Array.from(
    new Set(tokens.filter((token) => TEST_MARKETING_TYPES.includes(token)))
  );
};

const getTestHeaderBottomOffsetPx = (search) => {
  const params = new URLSearchParams(search);
  const rawOffset = params.get("testmktBottom");
  if (rawOffset === null || rawOffset.trim() === "") {
    return DEFAULT_TEST_HEADER_BOTTOM_OFFSET_PX;
  }

  const parsed = Number(rawOffset);

  if (!Number.isFinite(parsed) || parsed < 0) {
    return DEFAULT_TEST_HEADER_BOTTOM_OFFSET_PX;
  }

  return parsed;
};

const creativeFactories = {
  banner: ({ headerBottomOffsetPx }) => ({
    id: "testmkt-banner",
    enabled: true,
    priority: 100,
    format: "banner",
    placements: ["header_top"],
    asset: {
      type: "image",
      src: "/banners/banner.png",
      alt: "Test marketing banner",
    },
    target: TEST_MARKETING_TARGET,
    dismissible: false,
    headerBottomOffsetPx,
  }),
  popup: () => ({
    id: "testmkt-popup",
    enabled: true,
    priority: 90,
    format: "popup",
    asset: {
      type: "image",
      src: "/banners/popup.jpeg",
      alt: "Test marketing popup",
    },
    target: TEST_MARKETING_TARGET,
    dismissible: true,
    showCloseAfterSeconds: 0,
  }),
  video: () => ({
    id: "testmkt-video",
    enabled: true,
    priority: 80,
    format: "video",
    asset: {
      type: "video",
      src: "/banners/video.mp4",
      alt: "Test marketing video",
    },
    target: TEST_MARKETING_TARGET,
    autoplay: true,
    muted: true,
    playsInline: true,
    controls: false,
    dismissible: true,
    skipAfterSeconds: 0,
    closeOnEnd: false,
  }),
  ruleta: () => ({
    id: "testmkt-ruleta",
    enabled: true,
    priority: 95,
    format: "roulette",
    asset: {
      type: "image",
      src: "/banners/ruleta-front.png",
      discSrc: "/banners/ruleta-disc.png",
      alt: "Ruleta VIP",
    },
    prizes: TEST_ROULETTE_PRIZES,
    dismissible: true,
    showCloseAfterSeconds: 0,
    spinDurationMs: 3600,
  }),
};

export const buildTestMarketingConfig = (search) => {
  const requestedTypes = normalizeRequestedTypes(search);
  if (requestedTypes.length === 0) return null;
  const headerBottomOffsetPx = getTestHeaderBottomOffsetPx(search);

  return normalizeMarketingConfig({
    schemaVersion: 1,
    enabled: true,
    revision: `testmkt-${requestedTypes.join("-")}`,
    defaults: {
      frequency: {
        scope: "session",
        maxImpressions: 100,
        cooldownSeconds: 0,
      },
      target: {
        openIn: "new_tab",
      },
    },
    campaigns: [
      {
        id: "testmkt-campaign",
        enabled: true,
        priority: 100,
        triggers: [{ type: "page_view", delaySeconds: 0 }],
        creatives: requestedTypes.map((type) =>
          creativeFactories[type]({ headerBottomOffsetPx })
        ),
      },
    ],
  });
};
